import React, { useState } from 'react';
import axios from 'axios';
import './OrderDetail.css';

const COURIERS = ['로젠택배', '롯데택배', '우체국택배', '반값택배'];

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'badge-pending' },
  { value: 'accepted', label: 'Accepted', color: 'badge-accepted' },
  { value: 'quoted', label: 'Quote Sent', color: 'badge-quoted' },
  { value: 'payment_accepted', label: 'Payment Accepted', color: 'badge-payment_accepted' },
  { value: 'shipping', label: 'Shipping Out', color: 'badge-shipping' },
];

export default function OrderDetail({ order, onClose, onUpdate, onDelete, onQuote, showNotif }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    status: order.status,
    tracking_number: order.tracking_number || '',
    courier: order.courier || '',
    quote_amount: order.quote_amount || '',
    quote_currency: order.quote_currency || 'KRW',
    admin_notes: order.admin_notes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`/api/admin/orders/${order.id}`, form);
      showNotif('Order updated successfully');
      setEditing(false);
      onUpdate();
    } catch {
      showNotif('Failed to update order', 'error');
    } finally {
      setSaving(false);
    }
  };

  const si = STATUS_OPTIONS.find(s => s.value === order.status) || STATUS_OPTIONS[0];
  const formatDate = d => new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <div className="order-drawer">
        {/* Drawer Header */}
        <div className="drawer-header">
          <div>
            <span className="drawer-order-num">{order.order_number}</span>
            <span className={`badge ${si.color}`} style={{ marginLeft: 10 }}>{si.label}</span>
          </div>
          <div className="drawer-header-actions">
            <button className="btn btn-sm btn-outline" onClick={() => onQuote(order)}>💬 Quote</button>
            <button className="btn btn-sm btn-danger" onClick={() => { onDelete(order.id); onClose(); }}>🗑 Delete</button>
            <button className="drawer-close" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="drawer-body">
          {/* Customer Info */}
          <section className="detail-section">
            <h4 className="section-label">👤 Customer Information</h4>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-key">Name</span>
                <span className="detail-val">{order.customer_name}</span>
              </div>
              <div className="detail-item">
                <span className="detail-key">Email</span>
                <a href={`mailto:${order.customer_email}`} className="detail-val link">{order.customer_email}</a>
              </div>
              {order.customer_phone && (
                <div className="detail-item">
                  <span className="detail-key">Phone</span>
                  <span className="detail-val">{order.customer_phone}</span>
                </div>
              )}
              <div className="detail-item">
                <span className="detail-key">Submitted</span>
                <span className="detail-val">{formatDate(order.created_at)}</span>
              </div>
            </div>
          </section>

          {/* Product Info */}
          <section className="detail-section">
            <h4 className="section-label">📦 Product Details</h4>
            {order.image_path && (
              <div className="product-image-wrap">
                <img src={order.image_path} alt="Product" className="product-full-img" />
              </div>
            )}
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-key">Quantity</span>
                <span className="detail-val">{order.quantity}</span>
              </div>
              {order.product_url && (
                <div className="detail-item full">
                  <span className="detail-key">Product URL</span>
                  <a href={order.product_url} target="_blank" rel="noreferrer" className="detail-val link url-wrap">{order.product_url}</a>
                </div>
              )}
              {order.product_description && (
                <div className="detail-item full">
                  <span className="detail-key">Description</span>
                  <span className="detail-val">{order.product_description}</span>
                </div>
              )}
              {order.additional_notes && (
                <div className="detail-item full">
                  <span className="detail-key">Additional Notes</span>
                  <span className="detail-val">{order.additional_notes}</span>
                </div>
              )}
            </div>
          </section>

          {/* Order Management */}
          <section className="detail-section">
            <div className="section-label-row">
              <h4 className="section-label">⚙️ Order Management</h4>
              {!editing
                ? <button className="btn btn-sm btn-outline" onClick={() => setEditing(true)}>Edit</button>
                : <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-sm btn-outline" onClick={() => setEditing(false)}>Cancel</button>
                    <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}>
                      {saving ? <span className="spinner" style={{ width: 14, height: 14 }}></span> : 'Save'}
                    </button>
                  </div>
              }
            </div>

            {editing ? (
              <div className="edit-form">
                <div className="form-group">
                  <label>Status</label>
                  <select name="status" value={form.status} onChange={handleChange}>
                    {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div className="form-row-2">
                  <div className="form-group">
                    <label>Courier</label>
                    <select name="courier" value={form.courier} onChange={handleChange}>
                      <option value="">— Select —</option>
                      {COURIERS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Tracking Number</label>
                    <input type="text" name="tracking_number" value={form.tracking_number} onChange={handleChange} placeholder="Enter tracking #" />
                  </div>
                </div>
                <div className="form-row-2">
                  <div className="form-group">
                    <label>Quote Currency</label>
                    <select name="quote_currency" value={form.quote_currency} onChange={handleChange}>
                      <option value="KRW">₩ KRW</option>
                      <option value="USD">$ USD</option>
                      <option value="EUR">€ EUR</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Quote Amount</label>
                    <input type="number" name="quote_amount" value={form.quote_amount} onChange={handleChange} placeholder="0" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Admin Notes</label>
                  <textarea name="admin_notes" value={form.admin_notes} onChange={handleChange} rows={3} placeholder="Internal notes..." />
                </div>
              </div>
            ) : (
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-key">Status</span>
                  <span className={`badge ${si.color}`}>{si.label}</span>
                </div>
                {order.quote_amount && (
                  <div className="detail-item">
                    <span className="detail-key">Quote</span>
                    <span className="detail-val quote-val">
                      {order.quote_currency === 'KRW' ? '₩' : '$'}
                      {Number(order.quote_amount).toLocaleString()} {order.quote_currency}
                    </span>
                  </div>
                )}
                {order.courier && (
                  <div className="detail-item">
                    <span className="detail-key">Courier</span>
                    <span className="detail-val">{order.courier}</span>
                  </div>
                )}
                {order.tracking_number && (
                  <div className="detail-item">
                    <span className="detail-key">Tracking #</span>
                    <span className="detail-val tracking">{order.tracking_number}</span>
                  </div>
                )}
                {order.admin_notes && (
                  <div className="detail-item full">
                    <span className="detail-key">Admin Notes</span>
                    <span className="detail-val">{order.admin_notes}</span>
                  </div>
                )}
                {!order.quote_amount && !order.courier && !order.tracking_number && !order.admin_notes && (
                  <p className="no-data">No management data yet. Click Edit to update.</p>
                )}
              </div>
            )}
          </section>
        </div>

        {/* Quick Actions Footer */}
        <div className="drawer-footer">
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => onQuote(order)}>
            📧 Send Quote Email
          </button>
          <a href={`mailto:${order.customer_email}?subject=Re: Order ${order.order_number}`} className="btn btn-outline" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            ✉️ Email Customer
          </a>
        </div>
      </div>
    </>
  );
}
